/**
 * Real-time review stream — the typed client contract for the backend live-SSE
 * endpoint `GET /api/workspaces/:slug/stream`. Mirrors `src/realtime/events.zig`
 * EXACTLY: one SSE `event:` name per member of `STREAM_EVENTS`, each carrying a
 * JSON `data:` payload defined below.
 *
 * `parseStreamEvent` is total — a malformed / unknown frame yields `null`
 * (never throws), so one bad frame can never crash the reviewer's session.
 */

import type { Annotation } from "../api/types";

/** The SSE `event:` names the backend emits, in one place. */
export const STREAM_EVENTS = [
  "comment-created",
  "reply",
  "status-change",
  "activity",
  "comment-reaction",
] as const;

export type StreamEventName = (typeof STREAM_EVENTS)[number];

/**
 * A comment as carried on the live stream. The backend sends the full review
 * comment — the SAME shape the REST surface returns (`Annotation` = `Comment`,
 * with `parent_id` / `type` / `status` / anchor fields), so a live-appended
 * comment renders and threads exactly like a fetched one.
 */
export type LiveComment = Annotation;

/** `comment-created` / `reply` payload: the owning doc + the full comment. */
export interface CommentEnvelope {
  doc_id: number;
  comment: LiveComment;
}

/** `status-change` payload: which comment moved to which status, and by whom. */
export interface StatusEnvelope {
  doc_id: number;
  id: number;
  status: string;
  by: string;
}

/** `activity` payload: one workspace-wide activity-feed row. */
export interface ActivityEnvelope {
  kind: string;
  actor: string;
  doc_id: number;
  comment_id: number;
  status?: string | null;
}

/** `comment-reaction` payload (review-notes §4.5, PUBLIC comments only): the emoji
 *  + its NEW count on `comment_id`, plus who acted. A restricted comment's reaction
 *  never uses this event (the backend fans out a body-less `comment-refresh`
 *  instead), so this envelope only ever names a comment every recipient can see. */
export interface ReactionEnvelope {
  doc_id: number;
  comment_id: number;
  emoji: string;
  count: number;
  actor_id: number;
  actor_name: string;
  reacted_by_actor: boolean;
}

/** A parsed, typed stream event (discriminated by `name`). */
export type StreamEvent =
  | { name: "comment-created"; data: CommentEnvelope }
  | { name: "reply"; data: CommentEnvelope }
  | { name: "status-change"; data: StatusEnvelope }
  | { name: "activity"; data: ActivityEnvelope }
  | { name: "comment-reaction"; data: ReactionEnvelope };

function isStreamEventName(name: string): name is StreamEventName {
  return (STREAM_EVENTS as readonly string[]).includes(name);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * Parse a raw SSE frame (`event:` name + `data:` JSON) into a typed event, or
 * `null` if the name is unknown or the payload is malformed / missing the keys
 * the shape requires. Never throws.
 */
export function parseStreamEvent(name: string, raw: string): StreamEvent | null {
  if (!isStreamEventName(name)) return null;

  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(data)) return null;

  switch (name) {
    case "comment-created":
    case "reply": {
      const comment = data.comment;
      if (typeof data.doc_id !== "number" || !isRecord(comment)) return null;
      if (typeof comment.id !== "number") return null;
      return { name, data: data as unknown as CommentEnvelope };
    }
    case "status-change": {
      if (
        typeof data.doc_id !== "number" ||
        typeof data.id !== "number" ||
        typeof data.status !== "string"
      ) {
        return null;
      }
      return { name, data: data as unknown as StatusEnvelope };
    }
    case "activity": {
      if (
        typeof data.kind !== "string" ||
        typeof data.doc_id !== "number" ||
        typeof data.comment_id !== "number"
      ) {
        return null;
      }
      return { name, data: data as unknown as ActivityEnvelope };
    }
    case "comment-reaction": {
      if (
        typeof data.doc_id !== "number" ||
        typeof data.comment_id !== "number" ||
        typeof data.emoji !== "string" ||
        typeof data.count !== "number"
      ) {
        return null;
      }
      return { name, data: data as unknown as ReactionEnvelope };
    }
  }
}
